import {useMemo, useCallback, memo} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {UAParser} from 'ua-parser-js'

import Dialog from '@mui/material/Dialog'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import CameraIcon from '@mui/icons-material/Camera'

import FadeTransition from '../FadeTransition'
import SlideTransition from '../SlideTransition'


/** small hook to detect mobile once */
function useIsMobileDevice() {
  return useMemo(() => new UAParser().getDevice().type === 'mobile', [])
}

/** extracted gradient icon so React can memoize it */
const GradientCameraIcon = memo(({ fontSize }) => (
  <>
    <svg
      style={{position: 'absolute'}}
      width={0}
      height={0}
      xmlns="http://www.w3.org/2000/svg"
    >
      <linearGradient id="videGradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ff0095" />
        <stop offset="1" stopColor="#fbde54" />
      </linearGradient>
    </svg>
    <CameraIcon
      fontSize={fontSize}
      style={{
        color: 'transparent',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        fill: 'url(#videGradient)',
      }}
    />
  </>
))


const cssStyles = (color) => ({
  menuItem: css`
    color: ${color};
    svg { width: 24px; margin-right: 10px; }
    span { color: #434343; }
  `,
})


const VideoIconButton = ({
  variant = 'button',
  size: explicitSize,
  color = 'rgba(0,0,0,0.54)',
  open,
  setOpen,
  children,
  ...rest
}) => {
  const isXs = useMediaQuery(theme => theme.breakpoints.only('xs'))
  const isMobileDevice = useIsMobileDevice()

  // memoize styles & iconSize
  const styles = useMemo(() => cssStyles(color), [color])
  const iconSize = explicitSize || 'medium'

  // stable handlers
  const handleClick = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
    const vid = document.getElementById('profile-video')
    if (vid && !vid.paused && !vid.ended && vid.currentTime > 0) {
      vid.pause()
    }
  }, [setOpen])

  const handleClose = useCallback(() => setOpen(false), [setOpen])

  // render either a menu item or an icon button
  const Trigger = variant === 'menuItem' ? MenuItem : IconButton
  const triggerProps =
    variant === 'menuItem'
      ? { css: styles.menuItem }
      : { size: iconSize }

  return (
    <>
      <Trigger onClick={handleClick} {...triggerProps} {...rest}>
        {variant === 'menuItem' ? (
          <>
            <CameraIcon fontSize={iconSize} />
            <span>Video</span>
          </>
        ) : (
          <GradientCameraIcon fontSize={iconSize} />
        )}
      </Trigger>

      <Dialog
        open={open}
        scroll={isMobileDevice ? 'paper' : 'body'}
        onClose={handleClose}
        fullWidth
        fullScreen={isXs}
        TransitionComponent={isXs ? SlideTransition : FadeTransition}
        PaperProps={{
          onClick: (e) => e.stopPropagation()
        }}
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: isMobileDevice ? '#000' : undefined,
            
            '& .MuiPaper-root': {
              maxWidth: isMobileDevice ? '100vw' : 500,
              height: isMobileDevice ? '100%' : 'auto',
              backgroundColor: 'black',
              overflow: 'hidden',
            },
          },
        }}
      >
        {children}
      </Dialog>
    </>
  )
}

export default VideoIconButton

